/* @flow */
import * as React from 'react';
import { assertions } from 'lib/assertions';
import AuthActions from 'auth/AuthActions';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import ConfirmDevice from 'auth/components/ConfirmDevice';
import Cookies from 'lib/Cookies';
import DividerText from 'components/DividerText';
import Electron from 'lib/Electron';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import Text from 'components/Text';
import GoogleSignIn from 'auth/components/GoogleSignIn';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Notice from 'components/Notice';
import Row from 'components/Row';
import ToastActions from 'actions/ToastActions';
import Profile from 'entities/Profile';

import './styles/SignInForm.scss';

const mediumOfUid = (uid: string) => (uid.includes('@') ? 'email' : 'phone');

type Props = {
  initialUid?: string,
  emailConfirmationToken?: string,
};

type State = {
  signingIn: boolean,
  needsConfirmation: boolean,
  needsConfirmationErrorCode?: string,
  uid?: string,
  password?: string,
  medium: 'email' | 'phone',
  persist: boolean,
  needsPassword: boolean,
};

class SignInForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    // Flow seems to have trouble with getInitialState()
    const uid = props.initialUid || '';

    this.state = {
      signingIn: false,
      needsConfirmation: false,
      needsPassword: false,
      uid,
      password: '',
      medium: mediumOfUid(uid),
      persist: this.getPersistFromCookie(),
    };
  }

  componentDidMount() {
    EventTracking.trackKey('log_in.email_and_password_page.view');
  }

  submitIsDisabled = () => {
    return this.state.signingIn || !this.state.uid || !this.state.password;
  };

  togglePersistChange = () => {
    const newPersistValue = !this.state.persist;
    Cookies.set('persist', newPersistValue);
    this.setState({ persist: newPersistValue });
  };

  handlePasswordChange = (password?: string) => {
    this.setState({ password });
  };

  handleUidChange = (uid?: string) => {
    if (!uid) {
      this.setState({ uid });
      return;
    }

    const medium = mediumOfUid(uid);
    this.setState({
      uid,
      medium,
    });
  };

  handleProfileSuccess = (profile: Profile) => {
    if (profile.needsPassword()) {
      // send to partial account completion page
      this.setState({
        signingIn: false,
        needsConfirmation: true,
        needsPassword: true,
      });
    } else {
      this.handleInvalidCredentials();
    }
  };

  goBack = () => {
    this.setState({ needsConfirmation: false, signingIn: false });
  };

  getPersistFromCookie = () => {
    if (typeof Cookies.get('persist') === 'undefined') {
      const defaultValue = Electron.isAvailable() ? 'true' : 'false';
      Cookies.set('persist', defaultValue);
    }

    return Cookies.get('persist') === 'true';
  };

  // if an account exists and it needs a password
  // send them the confirmation email and redirect them to an instruction page
  checkForAccountExistence = () => {
    const { uid } = this.state;
    // No sense in checking if this is an existing account if the input is only whitespace
    if (uid && uid.trim()) {
      Profile.fetchByIdentifier(uid)
        .then(this.handleProfileSuccess)
        .catch(this.handleInvalidCredentials);
    } else {
      this.handleInvalidCredentials();
    }
  };

  handleInvalidCredentials = (err?: Object) => {
    const uidErr = 'Invalid email address or phone number';
    const unregistered = err && err.statusCode === 404;
    const errorMessage = unregistered ? uidErr : `${uidErr} or password`;

    ToastActions.showErrorToast(errorMessage);
    this.setState({ signingIn: false });
  };

  handleSubmit = () => {
    const { uid, password, persist } = this.state;
    const { emailConfirmationToken } = this.props;
    if (this.state.signingIn || !uid || !password) {
      return;
    }

    AuthActions.signIn(uid, password, emailConfirmationToken, persist).catch(err => {
      if (err && err.statusCode === 403) {
        this.setState({ needsConfirmation: true, needsConfirmationErrorCode: err.code });
      } else if (err && err.statusCode === 401) {
        this.checkForAccountExistence();
      } else {
        ToastActions.showErrorToast('There was an error logging in. Please try again later.');
        this.setState({ signingIn: false });
      }
    });

    this.setState({ signingIn: true });
  };

  renderCookieWarningIfNeeded = () => {
    if (Cookies.isEnabled()) {
      return null;
    }

    return (
      <Row>
        <Notice color="lava">
          Cookies are not enabled on your browser. Please enable cookies in your browser preferences
          to continue.
        </Notice>
      </Row>
    );
  };

  renderDefault = () => {
    return (
      <React.Fragment>
        <form
          data-test="sign-in-form"
          onSubmit={e => {
            e.preventDefault();
            this.handleSubmit();
          }}>
          {this.renderCookieWarningIfNeeded()}
          <FormElement
            data-test="uid-field"
            label="Email address or phone number"
            onValidChange={e => this.handleUidChange(e.target.value)}
            onInvalidChange={() => this.handleUidChange(undefined)}
            validate={e => {
              assertions.emailOrPlausibleNorthAmericanPhoneNumber(
                e.target.value,
                'Invalid phone number or email address. Please try again.'
              );
            }}
            element={Input}
            elementProps={{
              autocomplete: 'username',
              name: 'uid',
              type: 'username',
              placeholder: 'Email address or phone number',
              size: 'large',
              required: true,
              defaultValue: this.props.initialUid,
            }}
          />
          <FormElement
            data-test="password-field"
            label="Password"
            validate={e => {
              assertions.notEmpty(e.target.value, 'You must enter a password.');
            }}
            onValidChange={e => this.handlePasswordChange(e.target.value)}
            onInvalidChange={() => this.handlePasswordChange(undefined)}
            element={Input}
            elementProps={{
              autocomplete: 'current-password',
              name: 'password',
              placeholder: 'Password',
              size: 'large',
              required: true,
              autofocus: !!this.state.uid,
              type: 'password',
              append: (
                <Link
                  to="/forgot_password"
                  query={this.state.uid ? { email: this.state.uid } : null}
                  tabIndex="-1"
                  className="SignInForm-forgotPassword"
                  eventElementName="SignInForm-forgotPassword">
                  Forgot?
                </Link>
              ),
            }}
          />
          <Row size="small">
            <Checkbox
              data-test="persist-checkbox"
              name="persist"
              checked={this.state.persist}
              onChange={this.togglePersistChange}
              label="Stay logged in"
            />
          </Row>
          <Button
            data-test="submit-button"
            className="SignInForm-submit"
            eventElementName="SignInForm-submit"
            eventProperties={this.getPersistFromCookie() ? { state: 'stay_logged_in' } : {}}
            eventKey="log_in.email_and_password_page.log_in.click"
            type="submit"
            level="primary"
            full
            disabled={this.submitIsDisabled()}>
            Log in
          </Button>
        </form>

        <DividerText uppercase>or</DividerText>

        <GoogleSignIn
          mergeToken={this.props.emailConfirmationToken}
          eventKey="log_in.email_and_password_page.log_in_with_google.click"
        />

        <Link
          href={LinkConstants.SIGN_UP}
          target={Electron.isAvailable() ? '_blank' : null}
          color="brand"
          weight="semibold"
          className="SignInForm-signUpLink"
          eventElementName="SignInForm-signUpLink"
          eventKey="log_in.email_and_password_page.sign_up_link.click">
          <Text color="thunder">Don’t have an account? </Text>
          Sign up!
        </Link>
      </React.Fragment>
    );
  };

  render() {
    const {
      needsConfirmation,
      uid,
      needsPassword,
      medium,
      needsConfirmationErrorCode,
    } = this.state;
    if (needsConfirmation && uid) {
      return (
        <ConfirmDevice
          deviceType={medium}
          uid={uid}
          needsPassword={needsPassword}
          onGoBack={this.goBack}
          needsConfirmationErrorCode={needsConfirmationErrorCode}
        />
      );
    } else {
      return this.renderDefault();
    }
  }
}
export default SignInForm;
